/**
 * AdminCmsLayout — slim shell chrome for the `/admin/cms/*` authoring
 * surface (021-cms slice A1).
 *
 * The member `<Layout>` chrome (SkipLink + TopBar + main + PrimaryNav +
 * MembershipExpiredBanner + MenuSheet) is designed
 * around member wayfinding. The CMS authoring surface needs a focused
 * frame without that wayfinding bleeding through — the proto's admin
 * shell carries only a logo, a vertical divider, and a page title in
 * a ~52px top band. This component is the smallest shell that satisfies
 * that posture without coupling to member chrome.
 *
 * What this shell renders:
 *
 *   - `<SkipLink />` first, so keyboard users get the canonical
 *     "Siirry pääsisältöön" affordance the moment they Tab into the
 *     page (FR-005 keyboard-accessible skip link on every route).
 *   - `<header>` (banner landmark) with the YTK logo + a vertical
 *     hairline divider + the page-title `<h1>` + a right-aligned pill
 *     cluster (Päävalikko + Arkisto). The logo wraps in a `<Link to="/">`
 *     so the brand mark routes home (mirrors the member TopBar's
 *     brand-link affordance). The page title reads
 *     `t('cms.shell.title')` — Finnish-first "OMA+ sisällönhallinta",
 *     English "OMA+ content management" (Phase 18 / D-124). It is a
 *     dedicated shell-title key, kept distinct from `cms.catalog.title`
 *     (which the loading / error / viewport copy reuses). This
 *     deliberately renders ONE page-level `<h1>` for the entire admin
 *     shell; AdminCmsPage's own internal headings start at `<h2>` so
 *     heading-level order stays consistent. The back-to-portal
 *     `<Link to="/admin">` (a rounded-full outline pill, the right
 *     cluster's first entry) is the admin-area's FR-009 return
 *     affordance: the content-management section keeps its own shell
 *     and offers a first-class way back to the launch portal, with no
 *     second admin shell stacked over the CMS (025-admin-area US4 /
 *     FR-011). Its label reads `t('admin.cms.backToPortal')` — the
 *     proto's "Päävalikko" (Phase 18 / D-124), a CMS-only key distinct
 *     from `admin.feedback.backToPortal` (palautteet is unaffected).
 *   - `<main id="main-content" tabIndex={-1}>` carrying the route
 *     children. The id matches the SkipLink's hash target so
 *     activating the link moves focus into the landmark; the
 *     `tabIndex={-1}` makes the landmark programmatically focusable
 *     so the hash-jump actually receives keyboard focus.
 *
 * What this shell does NOT render (and the reasoning):
 *
 *   - No `<PrimaryNav>` (the member Koti / Viestit / Omat tiedot bottom
 *     nav). Admin authoring isn't a member surface —
 *     the bottom nav's targets aren't relevant in this context and
 *     would be misleading wayfinding.
 *   - No `<MembershipExpiredBanner>`. The banner self-gates on
 *     `state.kind === 'member' && ... membershipExpired`; admins
 *     never trip the gate, but skipping the mount avoids the
 *     unnecessary subscription to session state from a non-member
 *     surface.
 *   - No `<MenuSheet>` / hamburger. The member secondary-nav surface
 *     (sign-out, language toggle, admin link) is irrelevant inside
 *     the admin authoring shell — admin sessions are already in the
 *     admin context, and language switching for authoring lands as
 *     a future slice (US3 locale picker).
 *
 * Future slices (A2-A5) extend this header with the CMS-specific
 * controls (locale picker, "Siirry sivulle", "Versiot") as their
 * backing user stories ship. Those controls sit to the right of the
 * title alongside the right-hand control group, which today holds the
 * Arkisto drawer entry (024-cms US6 — see `ArchiveDrawer`) followed by
 * the back-to-portal affordance.
 *
 * Every utility class below is layout (`flex`, `gap-*`, `px-*`) or a
 * token-backed colour/spacing utility (`bg-canvas`, `bg-top-bar`,
 * `border-border`, `h-cms-top-bar`, `text-foreground`). No raw hex,
 * no `*-[…]` brackets (FR-047 / no-token-bypass).
 */

import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router';

import ytkLogo from '../assets/ytk-logo.png';
import { ArchiveDrawer } from '../components/cms/ArchiveDrawer.js';
import { ContentLocaleSelect } from '../components/cms/ContentLocaleSelect.js';
import { useTranslate } from '../i18n/useTranslate.js';

import { AdminSectionExitLink } from './AdminSectionExitLink.js';
import { SkipLink } from './SkipLink.js';

// `bg-canvas` is the proto's cool-grey page canvas (Phase 19 first-screen
// parity, AdminCms2Page @ 9393e6f8 L4436); the white panes sit on it.
const shellClasses = 'flex min-h-screen flex-col bg-canvas';
// The slim top band — full-width sticky header. `h-cms-top-bar` is the
// 3.25rem (52px) token added in `tokens.css`; the surface paints
// `bg-top-bar` (the same pure-white token the member TopBar uses) with
// a hairline `border-b` for visual separation from the off-white page
// body. `sticky top-0` keeps the bar visible during scroll without
// taking the bar out of normal flow (no body-padding compensation
// needed, unlike the fixed-positioned member TopBar).
const headerClasses =
  'sticky top-0 z-30 flex h-cms-top-bar items-center gap-3 ' +
  'border-b border-border bg-top-bar px-4';
// Logo link picks up the same focus-visible treatment the rest of the
// shell uses (ring-ring, ring-offset-2) so keyboard focus reads
// consistently across the app's chrome surfaces.
const brandLinkClasses =
  'inline-flex items-center rounded-md no-underline transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const brandLogoClasses = 'h-7 w-auto';
// The vertical hairline divider between the logo and the title. A
// pure-CSS line drawn with `w-px h-5` against the existing border
// token, kept `aria-hidden` because it carries no semantic meaning —
// the visual separation is purely decorative.
const dividerClasses = 'h-5 w-px bg-border';
// Page title — semantic `<h1>` for the route. `text-sm` matches the
// proto's compact authoring chrome (the title sits at the same weight
// as the member TopBar's brand wordmark but tighter). `font-semibold`
// + `tracking-tight` keep the title visually distinct from prose.
const titleClasses = 'm-0 text-sm font-semibold tracking-tight text-foreground';
// The header's right-hand control cluster: the CONTENT-language switcher, the
// Arkisto drawer entry (024-cms US6 / FR-024), then the Päävalikko portal exit
// (FR-009 / FR-040) as the RIGHTMOST action. Päävalikko holds the rightmost
// anchor on every section shell, so the CMS and the dashboards read the same
// way; the Arkisto action keeps its place immediately to its left. `ml-auto`
// pushes the cluster to the band's right edge, and the band's `px-4` puts that
// edge 16px from the viewport. The language switcher leads the cluster (it
// selects CONTENT, not the language the tool is labelled in). Versiohistoria
// (US11 deferred) stays ABSENT (P7).
//
// Päävalikko itself is `AdminSectionExitLink`, shared verbatim with
// `AdminPortalLayout`, so the pill's metrics cannot drift between shells.
const headerClusterClasses = 'ml-auto flex items-center gap-2';
// Main fills the remaining vertical space below the sticky header.
// `flex-1` claims the leftover height of the shell column; `flex
// flex-col min-h-0` turns `<main>` itself into a flex container so its
// children can use `flex-1` to fill the allocated height (the
// admin-CMS two-pane grid relies on this for proper viewport-anchored
// stretching even when the catalog content is short). `min-h-0`
// permits the flex item to shrink below its content size so internal
// `overflow-y-auto` regions can contain long catalog lists without
// pushing the shell column past the viewport.
const mainClasses = 'flex flex-1 flex-col min-h-0';

export interface AdminCmsLayoutProps {
  /**
   * Routed content rendered inside the `<main>` landmark.
   */
  readonly children?: ReactNode;
}

export function AdminCmsLayout({ children }: AdminCmsLayoutProps): JSX.Element {
  const t = useTranslate();

  return (
    <div className={shellClasses}>
      <SkipLink />
      <header className={headerClasses}>
        <Link to="/" className={brandLinkClasses}>
          <img src={ytkLogo} alt={t('common.brandFullName')} className={brandLogoClasses} />
        </Link>
        <span aria-hidden="true" className={dividerClasses} />
        <h1 className={titleClasses}>{t('cms.shell.title')}</h1>
        {/* Right-hand pill cluster: the Arkisto drawer entry (024-cms US6 /
            FR-024) followed by the Päävalikko portal exit (FR-040 /
            FR-009), which holds the rightmost anchor on every shell. */}
        <div className={headerClusterClasses}>
          <ContentLocaleSelect />
          <ArchiveDrawer />
          <AdminSectionExitLink />
        </div>
      </header>
      <main id="main-content" tabIndex={-1} className={mainClasses}>
        {children}
      </main>
    </div>
  );
}
